import React, { createContext, useContext, useEffect, useState } from 'react';

import { getQueryParams } from '../../utils/queryParams';

import { ChangePlanComponent } from '../../../components/molecules/ChangePlanComponent/ChangePlanComponent';
import { useUser } from '../../api/gql/hooks/useUser';
import { useChatHistory } from '../../api/rest/hooks/useChatHistory';
import { MessageSenderEnum, type CreateMessage, type Message } from '../../types/Message.type';
import type { Plan } from '../../types/Plan.type';
import type { User } from '../../types/User.type';
import { getRandomWelcomeMessage } from '../../utils/messages';
import { useCart } from '../cart/CartContext';
import type { Auth, ChatContextType, ChatbotContextProps } from './ChatContext.types';

const ChatContext = createContext<ChatContextType | null>(null);


export const ChatProvider: React.FC<ChatbotContextProps> = ({ children, initialUser, deliveryDate }) => {
    const { getUser } = useUser();
    const { getCartCount } = useCart();
    const { getChat, chatState } = useChatHistory();

    const cartQty = getCartCount();

    const [auth, setAuth] = useState<Auth>({});
    const [user, setUser] = useState<User | null>(null);
    const [showChangePlan, setShowChangePlan] = useState<boolean>(false);
    const [plan, setPlan] = useState<Plan | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [conversationState, setConversationState] = useState<string>('mealsRecommendations');
    const [messages, setMessages] = useState<Message[]>([]);

    useEffect(() => {
        if (auth.name && messages.length === 0) {
            const welcomeMessage = getRandomWelcomeMessage(auth.name);
            setMessages([{ type: MessageSenderEnum.ai, content: welcomeMessage }]);
        }
    }, [auth.name, messages.length]);

    useEffect(() => {
        const lastMessage = messages[messages.length - 1];

        if (
            plan?.mealsPerDelivery !== undefined &&
            cartQty >= plan.mealsPerDelivery &&
            lastMessage &&
            lastMessage.interaction !== 'plan-change'
        ) {
            console.log('Adding ChangePlanComponent', lastMessage?.interaction);

            addComponentMessage({
                content: "Based on your needs, here is your meal plan:",
                component: <ChangePlanComponent />,
                interaction: 'plan-change',
            });
        }
    }, [cartQty, plan?.mealsPerDelivery, messages]);

    useEffect(() => {
        if (!initialUser) {
            const params = getQueryParams();
            setAuth({
                name: params.name as string,
                state: params.state as string,
            });
        }
        if (initialUser) {
            setAuth(initialUser);
        }
    }, [initialUser]);

    useEffect(() => {
        if (auth.state && deliveryDate) {
            getChat(deliveryDate);
        }
    }, [deliveryDate, auth.state, getChat]);

    useEffect(() => {
        if (auth.state) {
            sessionStorage.setItem('state', auth.state);

            getUser().then(resp => {
                if (resp?.user) {
                    setUser(resp.user);
                }

                if (resp?.userPlan?.id) {
                    setSelectedPlan(resp?.userPlan as Plan);
                }
            });
        }
    }, [getUser, auth.state]);

    useEffect(() => {
        const messages = chatState?.conversation?.data?.messages;
        if (messages && auth.name) {
            if (messages.length > 0) {
                setMessages(messages);
            } else {
                setMessages([{ type: MessageSenderEnum.ai, content: getRandomWelcomeMessage(auth.name) }]);
            }
        }
    }, [auth.name, chatState]);

    const sendMessage = (message: Message) => {
        setMessages(prev => [...prev, message]);
    };

    const addComponentMessage = ({ content, component, interaction }: CreateMessage) => {
        const message: Message = {
            type: MessageSenderEnum.ai, content, component, interaction,
        };
        setMessages(prev => [...prev, message]);
    };

    const setLoader = (loading: boolean) => {
        setIsLoading(loading);
    }

    const toggleChangePlan = (value?: boolean) => {
        setShowChangePlan(value ?? !showChangePlan)
    }

    const setSelectedUser = (user: any) => {
        setUser(user);
    }

    const setSelectedPlan = (plan: Plan) => {
        setPlan(plan);
    }

    return (
        <ChatContext.Provider
            value={{
                messages,
                sendMessage,
                addComponentMessage, conversationState,
                setConversationState,
                setLoader, isLoading,
                deliveryDate,
                user, plan,
                setSelectedUser,
                setSelectedPlan,
                showChangePlan,
                toggleChangePlan
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};
export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};